/* @flow */

import React, { useState, useEffect, useRef } from "react";
import cn from "classnames";
import { Link, useLocation } from "react-router-dom";

import { useUi } from "helpers/ui";
import { useTranslate } from "@awardit/react-use-translate";
import { Button, Foldable } from "@crossroads/ui-components";
import Wrapper from "components/Wrapper";
import { MODE } from "state/view-mode";
import ChevronIcon from "icons/chevron-big.svg";
import { disableBodyScroll, enableBodyScroll } from "body-scroll-lock";
import useBrowserDimensions from "helpers/use-browser-dimensions";
import useBrowserLayoutEffect from "helpers/use-browser-layout-effect";

import styles from "./styles.scss";

type SubMenu = string | null;

type Props = {
  items: $ReadOnlyArray<ParentItem>,
  type: string,
};

type ParentProps = {
  item: ParentItem,
  subMenu: SubMenu,
  setSubMenu: SubMenu => void,
  type: string,
};

export type Item = {
  +name: string,
  +url: ?string,
};

export type ParentItem = {
  +name: string,
  +url: ?string,
  +children: $ReadOnlyArray<Item>,
};

type ItemProps = {
  className: string,
  item: Item,
  children: React$Node,
  setSubMenu: SubMenu => void,
};

const ChildItem = ({
  item,
  className = styles.item,
  setSubMenu,
  children,
}: ItemProps): React$Node => {
  const location = useLocation();
  const isParent = location.pathname === item.url;
  const { setViewMode: setMode, setHamburgerOpen } = useUi();

  return (!item.url || item.url === "/") ? (
    <div className={className}>{children}</div>
  ) : (
    <Link
      to={{
        pathname: item.url,
        state: { hint: { type: "category", category: item } },
      }}
      className={className}
      onClick={() => {
        if (isParent) {
          if (setMode) {
            setMode(MODE.NORMAL);
          }
          else {
            setHamburgerOpen(false);
          }
        }

        setSubMenu(null);
      }}
    >
      {children}
    </Link>
  );
};

const removeSpaces = (string: string) =>
  string.split(" ").join("");

const Parent = ({
  item,
  subMenu,
  setSubMenu,
  type,
}: ParentProps): React$Node => {
  const t = useTranslate();
  const toggleSubMenu = (name: string) => setSubMenu(subMenu === null ? name : null);
  const { width: browserWidth } = useBrowserDimensions();
  const tinySmallBreakpoint = Number.parseInt(styles.tinySmall, 10);
  const { viewMode } = useUi();

  return (
    <div className={cn(styles.row, styles.parent)}>
      <Wrapper className={styles.wrapper}>
        {item.children != null &&
          <div
            className={cn(styles.parent__item,
              { [styles.open]: subMenu === item.name }
            )}
          >
            <ChildItem
              item={item}
              setSubMenu={setSubMenu}
              className={cn(styles.heading, styles.heading__small)}
            >
              {item.name}
            </ChildItem>

            {item.children != null && item.children.length > 0 &&
              <Button
                className={styles.actionButton}
                aria-label={subMenu === item.name ? t("DRAWER.CLOSE_SUBMENU") : t("DRAWER.OPEN_SUBMENU")}
                aria-expanded={subMenu === item.name}
                aria-controls={removeSpaces(item.name) + "_children"}
                onClick={() => toggleSubMenu(item.name)}
              >
                <ChevronIcon
                  aria-label={t("ICONS.CHEVRON")}
                  className={cn(
                    styles.chevron,
                    { [styles.chevronOpen]: subMenu === item.name }
                  )} />
              </Button>
            }
          </div>
        }

        <ChildItem
          setSubMenu={setSubMenu}
          item={item}
          className={cn(styles.heading, styles.heading__large)}
        >
          {item.name}
          <ChevronIcon aria-label={t("ICONS.CHEVRON")} className={styles.chevron} />
        </ChildItem>

        <Foldable
          id={removeSpaces(item.name) + "_children"}
          wrapperClass={styles.items}
          open={
            (subMenu === item.name) ||
            (browserWidth > tinySmallBreakpoint && type === viewMode)}
        >
          {item.children !== undefined && item.children.map(child => (
            <ChildItem
              key={child.name}
              item={child}
              className={styles.item}
              setSubMenu={setSubMenu}
            >
              <div className={styles.item__heading}>
                <span>{child.name}</span>
              </div>
            </ChildItem>
          ))}
        </Foldable>
      </Wrapper>
    </div>
  );
};

const Drawer = ({ items, type }: Props): React$Node => {
  const [subMenu, setSubMenu] = useState<SubMenu>(null);
  const [windowInnerHeight, setWindowInnerHeight] = useState(null);
  const ref = useRef();
  const { viewMode, subNavOpen } = useUi();

  useEffect(() => {
    const element = ref.current;
    disableBodyScroll(element);

    return () => {
      if (element) {
        enableBodyScroll(element);
      }
    };
  }, []);

  useEffect(() => {
    const element = ref.current;

    if (subNavOpen) {
      element?.focus();
    }

    return () => {
      element?.blur();
    };
  }, [viewMode, subNavOpen]);

  useBrowserLayoutEffect(() => {
    const onResize = () => {
      setWindowInnerHeight(window.innerHeight);
    };

    if (window) {
      setWindowInnerHeight();
      window.addEventListener("resize", onResize, { passive: true });
      onResize();

      return () => {
        if (window) {
          window.removeEventListener("resize", onResize);
        }
      };
    }
  });

  return (
    <div
      ref={ref}
      style={windowInnerHeight ? { "--windowInnerHeight": `${windowInnerHeight}px` } : null}
      className={cn(styles.block, styles[type], styles.drawer)}
    >
      {items.map(x => (
        <Parent
          key={x.name}
          item={x}
          type={type}
          subMenu={subMenu}
          setSubMenu={setSubMenu} />
      ))}
    </div>
  );
};

export default Drawer;
